import { Modify } from "@biblioteksentralen/types";
import { colors } from "@biblioteksentralen/utils";
import { AlertRootProps, Alert as ChakraAlert, SystemStyleObject } from "@chakra-ui/react";
import React, { ReactNode } from "react";
import { AlertCircle, CheckCircle, Info, XCircle } from "react-feather";

type Status = "info" | "warning" | "success" | "error";
type CustomVariants = "inline";
type Variants = AlertRootProps["variant"] | CustomVariants;

const colorLookup: Record<Status, { bg: string; color: string }> = {
  info: { bg: colors.lightBlue, color: colors.accentBlueMedium },
  warning: { bg: colors.statusYellowLight, color: colors.statusYellow },
  success: { bg: colors.statusGreenLight, color: colors.statusGreen },
  error: { bg: colors.statusRedLight, color: colors.statusRed },
};

const iconLookup: Record<Status, React.ReactElement> = {
  info: <Info strokeWidth={1.75} role="img" aria-label="Informasjon" />,
  warning: <AlertCircle strokeWidth={1.75} role="img" aria-label="Advarsel" />,
  success: <CheckCircle strokeWidth={1.75} role="img" aria-label="Suksess" />,
  error: <XCircle strokeWidth={1.75} role="img" aria-label="Feil" />,
};

interface Props extends Modify<AlertRootProps, { variant?: Variants }> {
  children: ReactNode;
  header?: ReactNode;
  status: Status;
  variant?: Variants;
}

const variantStyles: Record<CustomVariants, SystemStyleObject> = {
  inline: {
    borderColor: "transparent",
    backgroundColor: "transparent",
    padding: "0",
  },
};

export function AlertBS({ status, children, variant, header, ...rest }: Props) {
  const colors = colorLookup[status];
  return (
    <ChakraAlert.Root
      status={status}
      fontSize="md"
      border={`0.1rem solid`}
      borderColor={colors.color}
      backgroundColor={colors.bg}
      display="flex"
      flexWrap="wrap"
      {...(variant === "inline" ? variantStyles[variant] : {})}
      {...rest}
    >
      <ChakraAlert.Indicator marginX="auto">{iconLookup[status]}</ChakraAlert.Indicator>
      <ChakraAlert.Content color="black">
        {header && <ChakraAlert.Title>{header}</ChakraAlert.Title>}
        <ChakraAlert.Description>{children}</ChakraAlert.Description>
      </ChakraAlert.Content>
    </ChakraAlert.Root>
  );
}
